import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { mapRoomsAdditionalParams } from 'src/app/helpers/utils/map.utils';
import { filterRoomsByFeatures } from 'src/app/helpers/utils/room-filter.utils';

import { RoomFilterCriteria } from 'src/app/modules/booking/components/filter-rooms/filter-rooms.models';
import { Room } from 'src/app/shared/models/room.model';

const DISPLAY_PAGE_SIZE = 20;

interface FilteredView {
  matched: Room[];
  others: Room[];
}

const isFilterActive = (criteria: RoomFilterCriteria): boolean =>
  !!(criteria.roomConfiguration || criteria.roomFeature);

const sameCriteria = (a: RoomFilterCriteria, b: RoomFilterCriteria): boolean =>
  a.roomConfiguration === b.roomConfiguration && a.roomFeature === b.roomFeature;

@Injectable({ providedIn: 'root' })
export class RoomStoreService {
  private readonly rawBuffer$ = new BehaviorSubject<Room[]>([]);
  private readonly displayOffset$ = new BehaviorSubject<number>(DISPLAY_PAGE_SIZE);
  private readonly nonMatchingOffset$ = new BehaviorSubject<number>(DISPLAY_PAGE_SIZE);
  private readonly filterCriteria$ = new BehaviorSubject<RoomFilterCriteria>({});

  private lastFoundRoom: Room | null = null;
  private lastDistanceValue = 0;
  private preFilterOffset: number | null = null;

  private readonly distinctCriteria$ = this.filterCriteria$.pipe(distinctUntilChanged(sameCriteria));

  // Single source of truth: compute matched + others once per (buffer, criteria) change.
  private readonly filteredView$: Observable<FilteredView> = combineLatest([
    this.rawBuffer$,
    this.distinctCriteria$
  ]).pipe(
    map(([buffer, criteria]) => {
      if (!isFilterActive(criteria)) {
        return { matched: buffer, others: [] };
      }
      const matched = filterRoomsByFeatures(buffer, criteria);
      const matchedSet = new Set(matched.map((r) => r.centreNumber));
      const others = buffer.filter((r) => !matchedSet.has(r.centreNumber));
      return { matched, others };
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly rooms$: Observable<Room[]> = combineLatest([this.filteredView$, this.displayOffset$]).pipe(
    map(([view, offset]) => mapRoomsAdditionalParams(view.matched.slice(0, offset))),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly matchedCount$: Observable<number> = this.filteredView$.pipe(
    map((view) => view.matched.length),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly nonMatchingRooms$: Observable<Room[]> = combineLatest([
    this.filteredView$,
    this.nonMatchingOffset$,
    this.distinctCriteria$
  ]).pipe(
    map(([view, offset, criteria]) => {
      if (!isFilterActive(criteria)) return [];
      return mapRoomsAdditionalParams(view.others.slice(0, offset));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public addRooms(result: Room[]): void {
    const current = this.rawBuffer$.value;
    const existing = new Set(current.map((r) => r.centreNumber));

    const newRooms = result
      .filter((room) => !existing.has(room.centreNumber))
      .map((room) => ({ ...room, distanceValue: this.lastDistanceValue++ }));

    if (newRooms.length > 0) {
      this.rawBuffer$.next([...current, ...newRooms]);
      this.lastFoundRoom = newRooms.at(-1);
    }
  }

  public reset(): void {
    this.rawBuffer$.next([]);
    this.displayOffset$.next(DISPLAY_PAGE_SIZE);
    this.nonMatchingOffset$.next(DISPLAY_PAGE_SIZE);
    this.filterCriteria$.next({});
    this.lastFoundRoom = null;
    this.lastDistanceValue = 0;
    this.preFilterOffset = null;
  }

  public setFilterCriteria(criteria: RoomFilterCriteria): void {
    if (sameCriteria(this.filterCriteria$.value, criteria)) {
      return;
    }

    const wasActive = isFilterActive(this.filterCriteria$.value);
    const willBeActive = isFilterActive(criteria);

    if (willBeActive && !wasActive) {
      this.preFilterOffset = this.displayOffset$.value;
    } else if (!willBeActive && wasActive) {
      this.displayOffset$.next(this.preFilterOffset ?? DISPLAY_PAGE_SIZE);
      this.preFilterOffset = null;
    }

    this.nonMatchingOffset$.next(DISPLAY_PAGE_SIZE);
    this.filterCriteria$.next(criteria);
  }

  public getFilterCriteria(): RoomFilterCriteria {
    return this.filterCriteria$.value;
  }

  public showNextPage(): boolean {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    const currentOffset = this.displayOffset$.value;

    if (filtered.length > currentOffset) {
      this.displayOffset$.next(currentOffset + DISPLAY_PAGE_SIZE);
      return true;
    }
    return false;
  }

  public hasMoreInBuffer(): boolean {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    return filtered.length > this.displayOffset$.value;
  }

  public showNextNonMatchingPage(): boolean {
    const criteria = this.filterCriteria$.value;
    if (!isFilterActive(criteria)) return false;

    const matched = new Set(filterRoomsByFeatures(this.rawBuffer$.value, criteria).map((r) => r.centreNumber));
    const otherCount = this.rawBuffer$.value.filter((r) => !matched.has(r.centreNumber)).length;
    const currentOffset = this.nonMatchingOffset$.value;

    if (otherCount > currentOffset) {
      this.nonMatchingOffset$.next(currentOffset + DISPLAY_PAGE_SIZE);
      return true;
    }
    return false;
  }

  public hasMoreNonMatchingInBuffer(): boolean {
    const criteria = this.filterCriteria$.value;
    if (!isFilterActive(criteria)) return false;

    const matched = new Set(filterRoomsByFeatures(this.rawBuffer$.value, criteria).map((r) => r.centreNumber));
    const otherCount = this.rawBuffer$.value.filter((r) => !matched.has(r.centreNumber)).length;
    return otherCount > this.nonMatchingOffset$.value;
  }

  public getNonMatchingRemainingCount(): number {
    const criteria = this.filterCriteria$.value;
    if (!isFilterActive(criteria)) return 0;

    const matched = new Set(filterRoomsByFeatures(this.rawBuffer$.value, criteria).map((r) => r.centreNumber));
    const otherCount = this.rawBuffer$.value.filter((r) => !matched.has(r.centreNumber)).length;
    return Math.max(0, otherCount - this.nonMatchingOffset$.value);
  }

  public getMatchedCount(): number {
    return filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value).length;
  }

  public getLastFoundRoom(): Room {
    return this.lastFoundRoom;
  }

  public getRemainingCount(): number {
    const filtered = filterRoomsByFeatures(this.rawBuffer$.value, this.filterCriteria$.value);
    return Math.max(0, filtered.length - this.displayOffset$.value);
  }
}
